import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  CalendarHeart,
  CalendarCheck,
  CalendarPlus,
  PhoneCall,
  MessageCircleMore,
  PenTool,
  Footprints,
  ShieldAlert,
  Pencil,
} from 'lucide-react';
import {toast} from "@sakura-grp/ui/sonner";
import {triggerHaptic} from "~/lib/haptics";
import type { CustomerDetailData } from '~/types/customer-detail.types';
import { useAppBackFlag } from '~/hooks/useAppBackFlag';
import { useHostessStore, getCurrentStoreId } from '@/stores/hostess';
import { useCreateActivity, type ActivityType } from '@/hooks/useActivities';
import { NgEditSheet } from './ng-edit-sheet';
import { NameEditSheet } from './name-edit-sheet';

interface CustomerDetailHeaderProps {
  data: CustomerDetailData;
}

export const CustomerDetailHeader = ({ data }: CustomerDetailHeaderProps) => {
  const navigate = useNavigate();
  const { markAppBack } = useAppBackFlag();
  const { hostess } = useHostessStore();
  const createActivity = useCreateActivity();
  const [showNgEdit, setShowNgEdit] = useState(false);
  const [showNameEdit, setShowNameEdit] = useState(false);

  // 営業活動を記録するハンドラー
  const handleActivityClick = (activityType: ActivityType) => {
    if (!hostess) {
      toast.error('ログイン情報を取得できませんでした');
      return;
    }
    const storeId = getCurrentStoreId(hostess);
    if (!storeId) {
      toast.error('店舗情報がありません');
      return;
    }

    triggerHaptic("light");
    createActivity.mutate({
      store_id: storeId,
      customer_id: data.id,
      hostess_id: hostess.id,
      activity_type: activityType,
    });
  };

  const getSalesStatusColor = () => {
    return data.salesStatus === 'good'
      ? { bg: 'bg-green-400', text: 'text-green-500' }
      : { bg: 'bg-red-400', text: 'text-red-500' };
  };

  const getSalesStatusLabel = () => {
    return data.salesStatus === 'good' ? '営業良好' : '営業不足';
  };

  const getVisitStatusColor = () => {
    if (!data.visitStatus) return null;
    switch (data.visitStatus) {
      case 'recent':
        return { bg: 'bg-green-400', text: 'text-green-500' };
      case 'moderate':
        return { bg: 'bg-warning', text: 'text-warning' };
      case 'old':
        return { bg: 'bg-red-400', text: 'text-red-500' };
    }
  };

  const getVisitStatusLabel = () => {
    if (!data.visitStatus) return null;
    switch (data.visitStatus) {
      case 'recent':
        return '3ヶ月以内に来店';
      case 'moderate':
        return '6ヶ月以内に来店';
      case 'old':
        return '6ヶ月以上来店なし';
    }
  };

  const statusColor = getSalesStatusColor();
  const visitStatusColor = getVisitStatusColor();
  const visitStatusLabel = getVisitStatusLabel();

  // 自分の所属店舗の顧客かどうか判定
  const currentStoreId = hostess ? getCurrentStoreId(hostess) : null;
  const isMyStoreCustomer = currentStoreId
    ? data.stores?.some((s) => s.storeId === currentStoreId)
    : false;

  return (
    <header>
      <div className="px-lg">
        <button
          type="button"
          className="block"
          onClick={() => {
            markAppBack();
            navigate(-1);
          }}
          aria-label="前のページに戻る"
        >
          <ChevronLeft className="-ms-md size-[2.7rem]" />
        </button>
      </div>
      <div className="px-lg pb-md flex items-center">
        <div className="me-auto">
          <h1 className="gap-sm flex items-center text-3xl">
            <span className="grid flex-none place-items-center">
              <span className="text-(length:--bigger-md) block">{data.icon}</span>
              {data.label && (
                <span className="text-(length:--smaller-2xl) ps-xs block">{data.label}</span>
              )}
              {data.points !== undefined && (
                <span className="text-(length:--smaller-lg) ps-xs block leading-none text-brand">
                  {data.points}pt
                </span>
              )}
            </span>
            <span className="space-y-sm block">
              {/* 最終来店ステータスバッジ */}
              {visitStatusColor && visitStatusLabel && (
                <span className="gap-sm flex items-center text-sm">
                  <span
                    className={`inline-block size-[1em] rounded-full ${visitStatusColor.bg}`}
                  ></span>
                  <span className={`font-bold ${visitStatusColor.text}`}>{visitStatusLabel}</span>
                </span>
              )}
              <span className="block">{data.name}</span>
              {data.kana && (
                <small className="text-(length:--smaller-xl) block">
                  ({data.kana})
                </small>
              )}
            </span>
          </h1>
          <ul className="gap-sm relative z-10 flex rounded-lg font-bold">
            <li className="gap-xs inline-flex items-center text-brand">
              <CalendarHeart className="size-[1.rem]" />
              <strong className="block text-xs font-bold tracking-normal">
                同伴({data.stats.dohan})
              </strong>
            </li>
            <li className="gap-xs inline-flex items-center text-brand">
              <CalendarCheck className="size-[1.rem]" />
              <strong className="block text-xs font-bold tracking-normal">
                確約({data.stats.kakuyaku})
              </strong>
            </li>
            <li className="gap-xs inline-flex items-center text-brand">
              <CalendarPlus className="size-[1.rem]" />
              <strong className="block text-xs font-bold tracking-normal">
                予約({data.stats.yoyaku})
              </strong>
            </li>
          </ul>
        </div>
        <div className="space-y-sm">
          {/* 編集系ボタン (氏名編集 + NG編集) */}
          <div className="gap-xs px-sm flex flex-wrap items-center">
            {isMyStoreCustomer && (
              <>
                <button
                  type="button"
                  onClick={() => setShowNameEdit(true)}
                  className="gap-xs flex items-center rounded-md border px-2 py-1 text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Pencil className="size-3.5" />
                  編集
                </button>
                <button
                  type="button"
                  onClick={() => setShowNgEdit(true)}
                  className="gap-xs flex items-center rounded-md border px-2 py-1 text-xs font-bold text-red-500 hover:bg-red-50 transition-colors"
                >
                  <ShieldAlert className="size-3.5" />
                  NG編集
                </button>
              </>
            )}
          </div>

          {/* 営業ステータス */}
          <span className="gap-xs px-sm flex items-center">
            <span className={`inline-block size-[1em] rounded-full ${statusColor.bg}`}></span>
            <span className={`text-xs font-bold ${statusColor.text}`}>
              {getSalesStatusLabel()}
            </span>
          </span>

          {/* アクションボタン（自分の所属店舗の顧客のみ表示） */}
          {isMyStoreCustomer && (
            <div className="gap-md pb-md flex items-center">
              {/* 電話ボタン */}
              <button
                type="button"
                onClick={() => handleActivityClick('phone')}
                disabled={createActivity.isPending || data.restrictions.phone}
                className="space-y-sm grid place-items-center active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100"
              >
                <div className="flex size-[2.6rem] items-center justify-center rounded-full border border-gray-400">
                  <PhoneCall className="size-[1.35rem]" />
                </div>
                <strong className="block text-xs font-bold leading-none tracking-tight text-gray-500">
                  電話<span className={data.restrictions.phone ? 'text-red-500' : 'text-green-600'}>
                    {data.restrictions.phone
                      ? 'NG'
                      : data.mobilePhone
                        ? `(${data.activityCounts?.phone ?? 0})`
                        : ''}
                  </span>
                  {!data.restrictions.phone && !data.mobilePhone && <span className="text-gray-400">番号なし</span>}
                </strong>
              </button>
              {/* メッセージボタン */}
              <button
                type="button"
                onClick={() => handleActivityClick('message')}
                disabled={createActivity.isPending || data.restrictions.message}
                className={`space-y-sm grid place-items-center active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100`}
              >
                <div className="flex size-[2.6rem] items-center justify-center rounded-full border border-gray-400">
                  <MessageCircleMore className="size-[1.35rem]" />
                </div>
                <strong className="block text-xs font-bold leading-none tracking-tight text-gray-500">
                  メッセ<span className={data.restrictions.message ? 'text-red-500' : 'text-green-600'}>
                    {data.restrictions.message ? 'NG' : `(${data.activityCounts?.message ?? 0})`}
                  </span>
                </strong>
              </button>
              {/* DMボタン */}
              <button
                type="button"
                onClick={() => handleActivityClick('letter')}
                disabled={createActivity.isPending || data.restrictions.dm}
                className="space-y-sm grid place-items-center active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100"
              >
                <div className="flex size-[2.6rem] items-center justify-center rounded-full border border-gray-400">
                  <PenTool className="size-[1.35rem]" />
                </div>
                <strong className="block text-xs font-bold leading-none tracking-tight text-gray-500">
                  DM<span className={data.restrictions.dm ? 'text-red-500' : 'text-green-600'}>
                    {data.restrictions.dm ? 'NG' : `(${data.activityCounts?.letter ?? 0})`}
                  </span>
                </strong>
              </button>
              {/* 訪問ボタン */}
              <button
                type="button"
                onClick={() => handleActivityClick('visit')}
                disabled={createActivity.isPending || data.restrictions.visit}
                className="space-y-sm grid place-items-center active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100"
              >
                <div className="flex size-[2.6rem] items-center justify-center rounded-full border border-gray-400">
                  <Footprints className="size-[1.35rem]" />
                </div>
                <strong className="block text-xs font-bold leading-none tracking-tight text-gray-500">
                  訪問<span className={data.restrictions.visit ? 'text-red-500' : 'text-green-600'}>
                    {data.restrictions.visit ? 'NG' : `(${data.activityCounts?.visit ?? 0})`}
                  </span>
                </strong>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 氏名編集シート */}
      <NameEditSheet
        open={showNameEdit}
        onOpenChange={setShowNameEdit}
        customerId={data.id}
        currentName={data.name}
        currentKana={data.kana || ''}
      />

      {/* NG編集シート */}
      <NgEditSheet
        open={showNgEdit}
        onOpenChange={setShowNgEdit}
        customerId={data.id}
        phoneNg={data.restrictions.phone}
        messageNg={data.restrictions.message}
        dmNg={data.restrictions.dm}
        visitNg={data.restrictions.visit}
      />
    </header>
  );
};
